import { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { thunks as SearchThunks } from 'reducers/Search'
import { SearchCategory } from 'constants/enums'

const PROFILE_SEARCH_PAGE_SIZE = 100

const emptySearchResponse = {
  tracks: [],
  labels: [],
  releases: [],
  artists: []
}

const emptyProfilePagination = {
  query: '',
  profile: undefined,
  page: 0,
  total: null,
  loadingMore: false
}

const getProfileSearchParams = (query, profile) => ({
  artist: profile === 'artist' ? query : undefined,
  label: profile === 'artist' ? undefined : query
})

const getTotalItems = (data, fallback) => {
  const total = Number(data?.total)
  return Number.isFinite(total) ? total : fallback
}

export const useSearchResults = (useAISearch = false) => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true)
  const [response, setResponse] = useState(emptySearchResponse)
  const [profilePagination, setProfilePagination] = useState(
    emptyProfilePagination
  )

  const update = useCallback(
    (query, category, profile, onError) => {
      const sentCategory =
        category !== SearchCategory.top ? category : undefined
      setResponse(emptySearchResponse)
      setProfilePagination(emptyProfilePagination)
      setLoading(true)

      if (profile) {
        const { artist, label } = getProfileSearchParams(query, profile)
        const handleError = () => {
          setLoading(false)
          if (onError) onError()
        }

        dispatch(
          SearchThunks.searchProfile(
            artist,
            label,
            (res) => {
              const items = res?.data?.items || []
              setResponse({
                tracks: [],
                labels: [],
                releases: items,
                artists: []
              })
              setProfilePagination({
                query,
                profile,
                page: 1,
                total: getTotalItems(res?.data, items.length),
                loadingMore: false
              })
              setLoading(false)
            },
            handleError,
            { page: 1, pagesize: PROFILE_SEARCH_PAGE_SIZE }
          )
        )
      } else {
        setProfilePagination(emptyProfilePagination)
        dispatch(
          SearchThunks.search(
            query,
            sentCategory,
            sentCategory ? 100 : 5,
            useAISearch,
            (res) => {
              setResponse({
                tracks: [],
                labels: [],
                releases: [],
                artists: [],
                ...res.data
              })
              setLoading(false)
            },
            onError
          )
        )
      }
    },
    [dispatch, useAISearch]
  )

  const hasMoreProfileResults =
    profilePagination.total !== null &&
    response.releases.length < profilePagination.total

  const loadMoreProfileResults = useCallback(
    (onError) => {
      if (
        !profilePagination.profile ||
        profilePagination.loadingMore ||
        !hasMoreProfileResults
      ) {
        return
      }

      const nextPage = profilePagination.page + 1
      const { artist, label } = getProfileSearchParams(
        profilePagination.query,
        profilePagination.profile
      )
      const handleError = () => {
        setProfilePagination((current) => ({
          ...current,
          loadingMore: false
        }))
        if (onError) onError()
      }

      setProfilePagination((current) => ({
        ...current,
        loadingMore: true
      }))

      dispatch(
        SearchThunks.searchProfile(
          artist,
          label,
          (res) => {
            const items = res?.data?.items || []
            setResponse((current) => ({
              ...current,
              releases: current.releases.concat(items)
            }))
            setProfilePagination((current) => ({
              ...current,
              page: nextPage,
              total: getTotalItems(res?.data, current.total),
              loadingMore: false
            }))
          },
          handleError,
          { page: nextPage, pagesize: PROFILE_SEARCH_PAGE_SIZE }
        )
      )
    },
    [dispatch, hasMoreProfileResults, profilePagination]
  )

  return {
    loading,
    response,
    setLoading,
    setResponse,
    update,
    profilePagination,
    hasMoreProfileResults,
    loadMoreProfileResults
  }
}

export default useSearchResults
